import { useRef, useState, type FocusEvent, type PointerEvent } from "react";

import type { Counts } from "../api/types";

const ORDER = ["job", "onsite", "base", "holiday", "doil", "off", "quarantine", "remaining"] as const;
type Kind = (typeof ORDER)[number];

const NAMES: Record<Kind, string> = {
  job: "On job",
  onsite: "On site (no job yet)",
  base: "Base day",
  holiday: "Holiday",
  doil: "DOIL used",
  off: "Off",
  quarantine: "Quarantine",
  remaining: "Not marked",
};
const GROUP: Record<Kind, string> = {
  job: "Counts as a worked day",
  onsite: "From your work calendar. Counts as a worked day",
  base: "Counts as a worked day",
  holiday: "Counts as a day not at work",
  doil: "Counts as a day not at work",
  off: "Counts as a day not at work",
  quarantine: "",
  remaining: "",
};

// Wedges smaller than this are labelled outside the pie, with a leader line.
const MIN_LABEL = 0.045;
// Half-width of the viewBox: the pie has radius 1, the rest is room for outside labels.
const VB = 1.5;
const SIZE = 230;
const GAP = 0.2; // minimum vertical spacing between outside labels on the same side

const pct = (f: number) => (f > 0 && f < 0.005 ? "<1%" : `${Math.round(f * 100)}%`);
const pct1 = (f: number) => `${(Math.round(f * 1000) / 10).toFixed(1)}%`;

interface Tip {
  k: Kind;
  x: number;
  y: number;
}

/** Pie of the year's days by type, coloured with the planner's status colours. */
export function DayPie({ counts, names = {} }: { counts: Counts; names?: Partial<Record<string, string>> }) {
  const wrap = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<Tip | null>(null);
  const total = ORDER.reduce((a, k) => a + Math.max(0, counts[k] || 0), 0) || 1;
  const marked = total - Math.max(0, counts.remaining || 0);
  const name = (k: Kind) => names[k] ?? NAMES[k];

  let a = -Math.PI / 2;
  const slices = ORDER.flatMap((k) => {
    const v = Math.max(0, counts[k] || 0);
    if (!v) return [];
    const frac = v / total;
    const b = a + frac * Math.PI * 2;
    const mid = (a + b) / 2;
    const slice = { k, v, frac, a, b, mid };
    a = b;
    return [slice];
  });

  // Outside labels: start level with the wedge, then push apart any that would overlap.
  const outside = new Map<Kind, { y: number; right: boolean }>();
  for (const right of [true, false]) {
    let prev = -Infinity;
    slices
      .filter((s) => s.frac < MIN_LABEL && Math.cos(s.mid) >= 0 === right)
      .map((s) => ({ k: s.k, y: Math.sin(s.mid) * 1.15 }))
      .sort((p, q) => p.y - q.y)
      .forEach((l) => {
        const y = Math.max(l.y, prev + GAP);
        outside.set(l.k, { y, right });
        prev = y;
      });
  }

  const showAt = (k: Kind) => (e: PointerEvent<SVGElement>) => {
    const r = wrap.current?.getBoundingClientRect();
    if (r) setTip({ k, x: e.clientX - r.left, y: e.clientY - r.top });
  };
  // Keyboard focus: put the tooltip over the wedge's centre.
  const focusAt = (k: Kind, mid: number) => (e: FocusEvent<SVGElement>) => {
    const svg = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
    const r = wrap.current?.getBoundingClientRect();
    if (!svg || !r) return;
    const half = svg.width / 2;
    const unit = half / VB;
    setTip({
      k,
      x: svg.left - r.left + half + Math.cos(mid) * unit * 0.6,
      y: svg.top - r.top + half + Math.sin(mid) * unit * 0.6,
    });
  };

  const active = tip && slices.find((s) => s.k === tip.k);
  const summary = slices.map((s) => `${name(s.k)} ${s.v} (${pct(s.frac)})`).join(", ");

  return (
    <div className="pie" ref={wrap} onPointerLeave={() => setTip(null)}>
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`${-VB} ${-VB} ${2 * VB} ${2 * VB}`}
        role="img"
        aria-label={`Days by type: ${summary}`}
      >
        {slices.map(({ k, v, frac, a, b, mid }) => {
          const out = outside.get(k);
          const fill = `var(--s-${k})`;
          const lift =
            tip?.k === k ? `translate(${Math.cos(mid) * 0.05} ${Math.sin(mid) * 0.05})` : undefined;
          const shape =
            frac >= 0.9999 ? (
              <circle r="1" />
            ) : (
              <path
                d={`M0 0 L${Math.cos(a)} ${Math.sin(a)} A1 1 0 ${frac > 0.5 ? 1 : 0} 1 ${Math.cos(b)} ${Math.sin(b)} Z`}
              />
            );
          return (
            <g
              key={k}
              className="wedge"
              fill={fill}
              stroke="var(--panel)"
              strokeWidth="0.02"
              transform={lift}
              tabIndex={0}
              aria-label={`${name(k)}: ${v} days, ${pct1(frac)} of the year`}
              onPointerMove={showAt(k)}
              onFocus={focusAt(k, mid)}
              onBlur={() => setTip(null)}
            >
              {shape}
              {out && (
                <>
                  <polyline
                    className="wedge-leader"
                    points={`${Math.cos(mid)},${Math.sin(mid)} ${Math.cos(mid) * 1.08},${Math.sin(mid) * 1.08} ${out.right ? 1.14 : -1.14},${out.y}`}
                  />
                  <text
                    x={out.right ? 1.17 : -1.17}
                    y={out.y}
                    className="wedge-label out"
                    style={{ textAnchor: out.right ? "start" : "end" }}
                    aria-hidden="true"
                  >
                    {pct(frac)}
                  </text>
                </>
              )}
              {frac >= MIN_LABEL && (
                <text
                  x={frac >= 0.9999 ? 0 : Math.cos(mid) * 0.64}
                  y={frac >= 0.9999 ? 0 : Math.sin(mid) * 0.64}
                  className="wedge-label"
                  aria-hidden="true"
                >
                  {pct(frac)}
                </text>
              )}
            </g>
          );
        })}
        <circle r="1" fill="none" stroke="var(--rule)" strokeWidth="0.015" pointerEvents="none" />
      </svg>
      {tip && active && (
        <div className="pie-tip" role="tooltip" style={{ left: tip.x, top: tip.y }}>
          <div className="pie-tip-head">
            <i className={`key-${tip.k}`} />
            {name(tip.k)}
          </div>
          <div>
            <b>{active.v}</b> {active.v === 1 ? "day" : "days"} · {pct1(active.frac)} of the year
          </div>
          {tip.k !== "remaining" && marked > 0 && (
            <div className="muted">{pct1(active.v / marked)} of the days marked so far</div>
          )}
          {GROUP[tip.k] && <div className="muted">{GROUP[tip.k]}</div>}
        </div>
      )}
    </div>
  );
}
